import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Printer, Copy, QrCode, Check } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_conecta-medicinas/artifacts/padqrbhb_Logo_CE_fondoBlanco.png";

export default function QRCodeDialog({ listingId, medicineName, price, zone, trigger }) {
  const [copied, setCopied] = useState(false);
  const canvasWrapRef = useRef(null);

  const url = `${window.location.origin}/medicamento/${listingId}?src=qr`;

  const handleDownload = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `qr-${(medicineName || "publicacion").replace(/\s+/g, "_")}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("QR descargado");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const handlePrint = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const w = window.open("", "_blank", "width=600,height=800");
    if (!w) return toast.error("Habilita ventanas emergentes para imprimir");
    w.document.write(`<!doctype html><html><head><title>QR — ${medicineName}</title>
      <style>
        body{font-family:Arial,sans-serif;text-align:center;padding:32px;color:#0F172A}
        h1{font-size:20px;margin:0 0 8px;color:#1E40AF}
        .meta{font-size:14px;color:#475569;margin-bottom:20px}
        img{max-width:300px;height:auto}
        .footer{margin-top:16px;font-size:12px;color:#64748B}
        .price{font-size:22px;font-weight:bold;color:#1E40AF;margin:8px 0}
        .brand{display:inline-flex;align-items:center;gap:8px;margin-top:14px}
        .brand img{height:36px;width:36px}
      </style></head>
      <body>
        <h1>${medicineName}</h1>
        ${price ? `<div class="price">$${price} MXN</div>` : ""}
        ${zone ? `<div class="meta">Zona: ${zone}</div>` : ""}
        <img src="${dataUrl}" alt="QR" />
        <div class="footer">Escanea con tu cámara para ver fotos y contactar por WhatsApp</div>
        <div class="brand">
          <img src="${LOGO_URL}" />
          <span><strong>Medicamentos_CiudadEnfermeria</strong></span>
        </div>
      </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 350);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" data-testid={`qr-trigger-${listingId}`}>
            <QrCode className="w-4 h-4 mr-1" /> QR
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid={`qr-dialog-${listingId}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-blue-950">Código QR de la publicación</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">
          Imprímelo en un volante o compártelo en tus grupos de WhatsApp. Cada escaneo lleva al detalle y se cuenta automáticamente.
        </p>

        <div ref={canvasWrapRef} className="mt-2 flex justify-center bg-white p-4 rounded-2xl border-2 border-blue-100">
          <QRCodeCanvas
            value={url}
            size={240}
            level="H"
            includeMargin
            bgColor="#FFFFFF"
            fgColor="#1E40AF"
            imageSettings={{
              src: LOGO_URL,
              height: 36,
              width: 36,
              excavate: true,
            }}
          />
        </div>

        <div className="mt-2 text-center">
          <div className="font-display text-base font-semibold text-slate-900 truncate">{medicineName}</div>
          {price && <div className="text-blue-900 font-bold">${price} MXN</div>}
        </div>

        <div className="mt-3 bg-slate-50 rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600 break-all" data-testid={`qr-url-${listingId}`}>
          {url}
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <Button variant="outline" onClick={handleCopy} data-testid={`qr-copy-${listingId}`}>
            {copied ? <Check className="w-4 h-4 mr-1 text-emerald-600" /> : <Copy className="w-4 h-4 mr-1" />}
            Copiar
          </Button>
          <Button variant="outline" onClick={handleDownload} data-testid={`qr-download-${listingId}`}>
            <Download className="w-4 h-4 mr-1" /> PNG
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={handlePrint} data-testid={`qr-print-${listingId}`}>
            <Printer className="w-4 h-4 mr-1" /> Imprimir
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
